import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, IconButton, TextField, Tooltip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOffOutlined from "@mui/icons-material/PersonOffOutlined";
import { toast } from "react-toastify";
import { getApplications, patchAdminApplication, patchApplication, scheduleMeeting } from "../../../redux/application/actions";
import { getApplicationsForJob } from "../../../redux/application/selectors";
import Table from "../../../components/shared/Table";
import { capitalizeFirstLetter } from "../../../utilities";
import PrimaryButton from "../../../components/shared/PrimaryButton";
import { AdminApplicationStatus, ApplicationStatus, Roles } from "../../../utilities/constants";
import { getCurrentUserSelector } from "../../../redux/users/selector";
import { setPreviousPath } from "../../../redux/utilities/actions";
import Filter from "../../../components/shared/Filter";
import { filterFormFields, filterFormSchema } from "./components/filterDetails";
import ConfirmationPopup from "../../../components/shared/ConfirmationPopup";

const ViewButton = ({ to, onClick }) => (
  <Link to={to} onClick={onClick} style={{ textDecoration: "none" }}>
    <PrimaryButton>View</PrimaryButton>
  </Link>
);

ViewButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  to: PropTypes.string.isRequired,
};

const StatusButton = ({ row, onClick, content, status }) => (
  <PrimaryButton onClick={onClick} sx={row.status === status && { background: "green" }}>
    {content}
  </PrimaryButton>
);

StatusButton.propTypes = {
  row: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  content: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
};

const getColumns = (user, dispatch, location, handleApplication, handleScheduleInterview, handleSelect) => [
  {
    id: "id",
    label: "ID",
    props: { align: "center" },
  },
  {
    id: "jobTitle",
    label: "Job Title",
    value: (row) => row.job.title,
    props: { align: "center" },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => row.user.email,
    condition: user?.role === Roles.ADMIN.value,
    props: { align: "center" },
  },
  {
    id: "status",
    label: "Status",
    value: (row) => ([Roles.ADMIN.value].includes(user?.role) ? AdminApplicationStatus.getLabel(row.adminApprovalStatus) : ApplicationStatus.getLabel(row.status)),
    props: { align: "center" },
  },
  {
    id: "address",
    label: "Location",
    value: (row) =>
      row.job?.address && row.job?.address?.country && row.job?.address?.city && row.job?.address?.zipCode && row.job?.address?.address
        ? `${row.job.address.country}, ${row.job.address.city} ${row.job.address.zipCode}, ${row.job.address.address}`
        : "",
  },
  {
    id: "startDate",
    label: "Start Date",
    value: (row) => moment(row.job.startDate).format("DD/MM/YYYY"),
    props: { align: "center" },
  },
  {
    id: "endDate",
    label: "End Date",
    value: (row) => moment(row.job.endDate).format("DD/MM/YYYY"),
    props: { align: "center" },
  },
  {
    id: "createdAt",
    label: "Application Date",
    value: (row) => capitalizeFirstLetter(moment(row.createdAt).fromNow()),
    props: { align: "center" },
  },
  {
    id: "view-button",
    value: (row) => (
      // <ViewButton
      //   onClick={() => dispatch(setPreviousPath(location.pathname + location.search))}
      //   to={[Roles.ADMIN.value, Roles.COMPANY.value].includes(user?.role) ? `/users/${row.user.id}` : `/jobs/${row.jobId}`}
      // />

      <Tooltip title="View" arrow>
        <Link to={[Roles.ADMIN.value, Roles.COMPANY.value].includes(user?.role) ? `/users/${row.user.id}` : `/jobs/${row.jobId}`} style={{ textDecoration: "none" }}>
          <IconButton onClick={() => dispatch(setPreviousPath(location.pathname + location.search))} size="small" style={{ border: "1px solid #cb4d52", borderRadius: "4px" }}>
            <VisibilityIcon />
          </IconButton>
        </Link>
      </Tooltip>
    ),
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "approve-button",
    value: (row) => (
      // <StatusButton
      //   onClick={() => handleApplication(AdminApplicationStatus.ADMIN_APPROVED_APPLICATION.value, row.id)}
      //   row={row}
      //   status={AdminApplicationStatus.ADMIN_APPROVED_APPLICATION.value}
      //   content="Approve"
      // />

      <Tooltip title="Accept" arrow>
        <IconButton
          onClick={() => handleApplication(AdminApplicationStatus.ADMIN_APPROVED_APPLICATION.value, row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === AdminApplicationStatus.ADMIN_APPROVED_APPLICATION.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <CheckCircleIcon />
        </IconButton>
      </Tooltip>
    ),
    condition: [Roles.ADMIN.value].includes(user?.role),
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "reject-button",
    value: (row) => (
      // <StatusButton
      //   onClick={() => handleApplication(AdminApplicationStatus.ADMIN_REJECTED_APPLICATION.value, row.id)}
      //   row={row}
      //   status={AdminApplicationStatus.ADMIN_REJECTED_APPLICATION.value}
      //   content="Reject"
      // />

      <Tooltip title="Decline" arrow>
        <IconButton
          onClick={() => handleApplication(AdminApplicationStatus.ADMIN_REJECTED_APPLICATION.value, row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === AdminApplicationStatus.ADMIN_REJECTED_APPLICATION.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <CancelIcon />
        </IconButton>
      </Tooltip>
    ),
    condition: [Roles.ADMIN.value].includes(user?.role),
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "accept-button",
    value: (row) => (
      // <StatusButton onClick={() => handleApplication(ApplicationStatus.ACCEPTED.value, row.id)} row={row} status={ApplicationStatus.ACCEPTED.value} content="Accept" />,

      <Tooltip title="Accept" arrow>
        <IconButton
          onClick={() => handleApplication(ApplicationStatus.ACCEPTED.value, row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === ApplicationStatus.ACCEPTED.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <CheckCircleIcon />
        </IconButton>
      </Tooltip>
    ),
    condition: (row) =>
      [Roles.COMPANY.value].includes(user?.role) &&
      row.status !== ApplicationStatus.MEETING_SCHEDULED.value &&
      row.status !== ApplicationStatus.SELECTED.value &&
      row.status !== ApplicationStatus.NOT_SELECTED.value,
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "decline-button",
    value: (row) => (
      // <StatusButton onClick={() => handleApplication(ApplicationStatus.REJECTED.value, row.id)} row={row} status={ApplicationStatus.REJECTED.value} content="Decline" />,

      <Tooltip title="Decline" arrow>
        <IconButton
          onClick={() => handleApplication(ApplicationStatus.REJECTED.value, row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === ApplicationStatus.REJECTED.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <CancelIcon />
        </IconButton>
      </Tooltip>
    ),
    condition: (row) =>
      [Roles.COMPANY.value].includes(user?.role) &&
      row.status !== ApplicationStatus.MEETING_SCHEDULED.value &&
      row.status !== ApplicationStatus.SELECTED.value &&
      row.status !== ApplicationStatus.NOT_SELECTED.value,
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "interview-button",
    value: (row) => (
      // <StatusButton onClick={() => handleScheduleInterview(row.id)} row={row} status={ApplicationStatus.MEETING_SCHEDULED.value} content="Schedule" />,

      <Tooltip title="Schedule an Interview" arrow>
        <IconButton
          onClick={() => handleScheduleInterview(row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === ApplicationStatus.MEETING_SCHEDULED.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <ScheduleIcon />
        </IconButton>
      </Tooltip>
    ),
    condition: (row) =>
      [Roles.COMPANY.value].includes(user?.role) &&
      (row.status === ApplicationStatus.ACCEPTED.value || row.status === ApplicationStatus.MEETING_SCHEDULED.value) &&
      row.status !== ApplicationStatus.SELECTED.value &&
      row.status !== ApplicationStatus.NOT_SELECTED.value,
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "selected-button",
    value: (row) => (
      // <StatusButton onClick={() => handleSelect(ApplicationStatus.SELECTED.value, row.id)} row={row} status={ApplicationStatus.SELECTED.value} content="Selected" />,

      <Tooltip title="Selected" arrow>
        <IconButton
          onClick={() => handleSelect(ApplicationStatus.SELECTED.value, row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === ApplicationStatus.SELECTED.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <HowToRegOutlinedIcon />
        </IconButton>
      </Tooltip>
    ),
    condition: (row) =>
      [Roles.COMPANY.value].includes(user?.role) &&
      row.status === ApplicationStatus.MEETING_SCHEDULED.value &&
      row.status !== ApplicationStatus.SELECTED.value &&
      row.status !== ApplicationStatus.NOT_SELECTED.value,
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
  {
    id: "not-selected-button",
    value: (row) => (
      // <StatusButton onClick={() => handleSelect(ApplicationStatus.NOT_SELECTED.value, row.id)} row={row} status={ApplicationStatus.NOT_SELECTED.value} content="Not-Selected" />,

      <Tooltip title="Not-Selected" arrow>
        <IconButton
          onClick={() => handleSelect(ApplicationStatus.NOT_SELECTED.value, row.id)}
          size="small"
          sx={{
            border: "1px solid #cb4d52",
            borderRadius: "4px",

            ...(row.status === ApplicationStatus.NOT_SELECTED.value && {
              backgroundColor: "#cb4d52",
              color: "white",

              "&:hover": {
                backgroundColor: "#cb4d52",
              },
            }),
          }}>
          <PersonOffOutlined />
        </IconButton>
      </Tooltip>
    ),
    condition: (row) =>
      [Roles.COMPANY.value].includes(user?.role) &&
      row.status === ApplicationStatus.MEETING_SCHEDULED.value &&
      row.status !== ApplicationStatus.SELECTED.value &&
      row.status !== ApplicationStatus.NOT_SELECTED.value,
    type: "button",
    props: { size: "small", align: "right", sx: { width: "2%" } },
  },
];

const Applications = ({ jobId }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.applications.loading);
  const [columns, setColumns] = useState(null);
  const user = useSelector(getCurrentUserSelector);
  const applications = useSelector(jobId ? (state) => getApplicationsForJob(state)(jobId) : (state) => state.applications.data);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const location = useLocation();
  const [jobAppId, setJobAppId] = useState(0);
  const [scheduleInterview, setScheduleInterview] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [feedback, setFeedback] = useState("");

  const feedbackOptions = [
    { value: "EXCELLENT", label: "Excellent", emoji: "😍" },
    { value: "VERY_GOOD", label: "Very Good", emoji: "😊" },
    { value: "GOOD", label: "Good", emoji: "🙂" },
    { value: "AVERAGE", label: "Average", emoji: "😐" },
    { value: "BAD", label: "Bad", emoji: "😞" },
  ];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      interviewDate: "",
      interviewTime: "",
      interviewLink: "",
    },
  });

  const {
    control: noteControl,
    handleSubmit: handleSubmitNote,
    reset: resetNote,
  } = useForm({
    defaultValues: {
      note: "",
    },
  });

  const handleApplication = (accept, id) => {
    dispatch([Roles.ADMIN.value].includes(user?.role) ? patchAdminApplication({ id, adminApprovalStatus: accept }) : patchApplication({ id, status: accept }))
      .then(() => {
        toast.success("Application has been accepted.");
      })
      .catch((error) => console.error(error));
  };

  const handleScheduleInterview = (id) => {
    setJobAppId(id);
    setScheduleInterview(true);
    reset({
      interviewDate: "",
      interviewTime: "",
      interviewLink: "",
    });
  };

  const handleClose = () => {
    setJobAppId(0);
    setScheduleInterview(false);
    setSelectedStatus("");
  };

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const handleSchedule = (data) => {
    const { interviewDate, interviewTime, interviewLink } = data;

    const meetingAt = dayjs(`${interviewDate} ${interviewTime}`).tz(dayjs.tz.guess()).format();

    dispatch(
      scheduleMeeting({
        id: jobAppId,
        meetingAt,
        meetingLink: interviewLink,
      }),
    ).then(() => {
      toast.success("Meeting scheduled successfully.");
      handleClose();
    });
  };

  const handleStatusChange = (data) => {
    dispatch(patchApplication({ id: jobAppId, status: selectedStatus, feedbackNote: data?.note || null, applicationRating: feedback })).then(() => {
      handleClose();
    });
  };

  const handleSelect = (status, id) => {
    setJobAppId(id);
    setSelectedStatus(status);
  };

  useEffect(() => {
    dispatch(getApplications(jobId !== null ? { jobId } : {}));
  }, [dispatch, jobId]);

  useEffect(() => {
    setFilteredApplications(applications);
  }, [applications]);

  useEffect(() => {
    if (!columns && user) {
      setColumns(getColumns(user, dispatch, location, handleApplication, handleScheduleInterview, handleSelect));
    }
  }, [user, columns, dispatch, location]);

  return (
    columns && (
      <Box>
        <Box>
          <Filter filterSteps={filterFormFields(user.role)} filterSchema={filterFormSchema} items={applications} setFilteredItems={setFilteredApplications} />
        </Box>
        <Table columns={columns} data={filteredApplications || []} isLoading={isLoading} noResultsContent="There are no applications." />

        <ConfirmationPopup open={jobAppId > 0 && scheduleInterview} handleClose={handleClose} handleSubmit={handleSubmit(handleSchedule)} title="Schedule an Interview" buttonText="Schedule">
          {/* DATE */}
          <Controller
            name="interviewDate"
            control={control}
            rules={{ required: "Interview date is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                type="date"
                label="Interview Date"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dayjs().format("YYYY-MM-DD"), // only future date
                }}
                error={!!errors.interviewDate}
                helperText={errors.interviewDate?.message}
                fullWidth
              />
            )}
          />

          {/* TIME */}
          <Controller
            name="interviewTime"
            control={control}
            rules={{ required: "Interview time is required" }}
            render={({ field }) => (
              <TextField {...field} type="time" label="Interview Time" InputLabelProps={{ shrink: true }} error={!!errors.interviewTime} helperText={errors.interviewTime?.message} fullWidth />
            )}
          />

          {/* LINK */}
          <Controller
            name="interviewLink"
            control={control}
            rules={{ required: "Interview link is required" }}
            render={({ field }) => (
              <TextField {...field} label="Interview Link" placeholder="Enter interview link" error={!!errors.interviewLink} helperText={errors.interviewLink?.message} fullWidth />
            )}
          />
        </ConfirmationPopup>

        <ConfirmationPopup
          open={jobAppId > 0 && selectedStatus !== ""}
          handleClose={() => {
            setSelectedStatus("");
            setJobAppId(0);
            resetNote();
            setFeedback("");
          }}
          handleSubmit={handleSubmitNote(handleStatusChange)}
          title={`Note for ${selectedStatus === ApplicationStatus.SELECTED.value ? "Selection" : "Not Selection"}`}
          buttonText="Submit">
          {/* Emoji Feedback */}
          <Box mb={2}>
            <Box display="flex" gap={1} flexWrap="wrap">
              {feedbackOptions.map((item) => (
                <Button
                  key={item.value}
                  variant={feedback === item.value ? "contained" : "outlined"}
                  onClick={() => setFeedback(item.value)}
                  sx={{
                    minWidth: 90,
                    display: "flex",
                    flexDirection: "column",
                    fontSize: "14px",
                  }}>
                  <span style={{ fontSize: "20px" }}>{item.emoji}</span>
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Note field */}
          <Controller name="note" control={noteControl} render={({ field }) => <TextField {...field} label="Note (optional)" placeholder="Add a note (optional)" multiline rows={3} fullWidth />} />
        </ConfirmationPopup>
      </Box>
    )
  );
};

Applications.propTypes = {
  jobId: PropTypes.number,
};

Applications.defaultProps = {
  jobId: null,
};

export default Applications;
